import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/react';
import type { User } from '@/types';
import { authApi } from '@/lib';
import { configSettings } from '@/lib/config';
import { SUBSCRIPTION_STATUS } from '@/features/subscriptions/constants';
import { useGlobalStore } from './globalStore';
import { useStoreStore } from './storeStore';
import { useNavigationStore } from './navigationStore';
import { useAskStanleyStore } from './askStanleyStore';
import { queryClient } from '@/queries';
import { ASK_STANLEY_CONSTANTS } from '@/features/ask-stanley';
import { LoginResponse } from '@/lib/api/auth/types';

/** Cookie Helpers --------------------------------------------------------- */

/**
 * Build cookie options that match the Vue app's domain strategy.
 *
 * Both apps (v1 at admin.stan.store and v2 at v2.admin.stan.store) must
 * write cookies to the SAME domain scopes so that the browser always
 * serves a single, consistent value regardless of which subdomain is
 * reading the cookie.
 *
 * Vue sets cookies on:
 *   - `.${appDomainNoProtocol}`      (e.g. `.admin.stan.store`)
 *   - `.${v2AppDomainNoProtocol}`    (e.g. `.v2.admin.stan.store`)
 *
 * React must do the same.
 */
const buildCookieOptions = (base: Cookies.CookieAttributes = {}, forV2Domain = false): Cookies.CookieAttributes => {
    const opts: Cookies.CookieAttributes = { path: '/', ...base };

    if (forV2Domain) {
        // v2 domain (e.g. .v2.admin.stan.store)
        opts.domain = configSettings.noHttpsAdminDomain ? `.${configSettings.noHttpsAdminDomain}` : undefined;
    } else {
        // v1 domain (e.g. .admin.stan.store) — also readable by v2 since it's a parent domain
        opts.domain = configSettings.noHttpsAdminLegacyDomain
            ? `.${configSettings.noHttpsAdminLegacyDomain}`
            : undefined;
    }

    return opts;
};

const setCookieForAllDomains = (name: string, value: string, options?: Cookies.CookieAttributes) => {
    // Set for v1 domain (e.g. .admin.stan.store) — readable by admin.stan.store
    Cookies.set(name, value, buildCookieOptions(options, false));
    // Set for v2 domain (e.g. .v2.admin.stan.store) — readable by v2.admin.stan.store
    Cookies.set(name, value, buildCookieOptions(options, true));
};

const removeCookieFromAllDomains = (name: string) => {
    // Remove host-only cookie (if it exists)
    Cookies.remove(name);

    // Remove from v1 domain
    Cookies.remove(name, buildCookieOptions({}, false));
    // Remove from v2 domain
    Cookies.remove(name, buildCookieOptions({}, true));

    // Clean up legacy cookies that were previously set on top-level domain
    // (stan.store) by the old React cookie helpers.
    // These stale cookies can shadow the correct subdomain-scoped cookies.
    Cookies.remove(name, { domain: 'stan.store', path: '/' });
};

/** Helpers --------------------------------------------------------------- */
const getKeyFromToken = (token?: string | null) => {
    if (!token) {
        return null;
    }
    try {
        const decoded: { sub?: string } = jwtDecode(token);
        return decoded?.sub ? `su_${decoded.sub}` : null;
    } catch {
        return null;
    }
};

const getScopesFromToken = (token?: string | null): string[] => {
    if (!token) {
        return [];
    }
    try {
        const decoded: { scopes?: string[] } = jwtDecode(token);
        return decoded?.scopes ?? [];
    } catch {
        return [];
    }
};

type Account = {
    token: string;
    expires: string;
    user_id?: string | number;
    username?: string;
    email?: string;
    image?: string;
    fullName?: string;
    tmpLoginAsAdmin?: boolean;
    admin?: boolean;
};

/** Store shape ----------------------------------------------------------- */
interface AuthStore {
    // state
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    storeInitialized: boolean;
    // MFA-specific
    mfaLoginEndpoint?: string;
    mfaToken?: string;
    mfaConfigName?: string;
    currentAccountKey: string | null;
    lastAccountKey: string | null;
    accounts: Record<string, Account>;
    hasActiveSubscription: boolean;

    // actions
    initialize: () => void;
    setLoginApiData: (data: LoginResponse) => void;
    logout: () => Promise<void>;
    clearError: () => void;
    invalidateToken: () => void;
    prepareToAddAccount: () => void;
    switchAccount: (key: string) => void;
    updateCurrentAccountWithUserData: (user: User) => void;
    saveToken: (token: string, options?: { tmpLoginAsAdmin?: boolean }) => void;
    // getters
    isAdminLoggedInAsUser: () => boolean;
    currentAccount: () => Account | null;
    accountScopesInclude: (scopes: string[] | string, accountKey?: string) => boolean;
}

export const useAuthStore = create<AuthStore>()(
    devtools(
        persist(
            (set, get) => ({
                token: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
                storeInitialized: false,
                currentAccountKey: null,
                lastAccountKey: null,
                accounts: {},
                hasActiveSubscription: false,

                // Add an initialization method for client side
                initialize: () => {
                    if (get().storeInitialized) return;

                    const token = Cookies.get('token') ?? null;
                    const currentAccountKey = Cookies.get('current_account_key') ?? getKeyFromToken(token);
                    const lastAccountKey = Cookies.get('last_account_key') ?? null;
                    const accounts = Object.keys(Cookies.get())
                        .filter(key => /^su_.*/.test(key))
                        .reduce((obj, key) => ({ ...obj, [key]: JSON.parse(Cookies.get(key) || '{}') }), {});

                    set({
                        token,
                        isAuthenticated: !!token,
                        currentAccountKey,
                        lastAccountKey,
                        storeInitialized: true,
                        accounts,
                    });
                },

                setLoginApiData: (data: LoginResponse) => {
                    if (data?.mfa_token) {
                        set({
                            mfaLoginEndpoint: data.route?.replace('/api/', ''),
                            mfaToken: data.mfa_token,
                            mfaConfigName: data.config_name,
                            isAuthenticated: false,
                        });
                    } else if (data?.token) {
                        const token: string = data.token;
                        get().saveToken(token);
                    } else {
                        throw new Error('No token or mfa token found');
                    }
                    useGlobalStore.getState().setDeviceToken(data.deviceToken ?? '');
                    return false;
                },

                logout: async () => {
                    const currentAccountKey = Cookies.get('current_account_key');
                    if (currentAccountKey) {
                        removeCookieFromAllDomains(currentAccountKey);
                        removeCookieFromAllDomains('current_account_key');
                    }

                    try {
                        await authApi.post(
                            'v1/logout',
                            {},
                            {
                                headers: { Authorization: `Bearer ${get().token}` },
                            }
                        );
                    } catch {
                        console.warn('no token');
                    }
                    removeCookieFromAllDomains('token');
                    set({
                        token: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: null,
                        mfaLoginEndpoint: undefined,
                        mfaToken: undefined,
                        mfaConfigName: undefined,
                        accounts: {},
                    });
                    queryClient.clear();
                },

                clearError: () => set({ error: null }),

                invalidateToken: () => {
                    removeCookieFromAllDomains('token');
                    const currentAccountKey = Cookies.get('current_account_key');
                    if (currentAccountKey) {
                        removeCookieFromAllDomains(currentAccountKey);
                        removeCookieFromAllDomains('current_account_key');
                    }

                    set({
                        token: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: 'Session expired. Please login again.',
                        mfaLoginEndpoint: undefined,
                        mfaToken: undefined,
                        mfaConfigName: undefined,
                    });
                    queryClient.clear();
                },

                saveToken: (token: string, options?: { tmpLoginAsAdmin?: boolean }) => {
                    const currentAccountKey = get().currentAccountKey;
                    const expires = new Date();
                    expires.setDate(expires.getDate() + 30);

                    const accounts = get().accounts;
                    const key = getKeyFromToken(token);
                    if (!key) return;

                    const account: Account = {
                        token,
                        expires: expires.toISOString(),
                        tmpLoginAsAdmin: options?.tmpLoginAsAdmin || false,
                    };
                    set({
                        token,
                        accounts: { ...accounts, [key]: account },
                        lastAccountKey: currentAccountKey,
                        currentAccountKey: key,
                        isAuthenticated: true,
                    });
                    setCookieForAllDomains(key, JSON.stringify(account), { expires });
                    setCookieForAllDomains('token', token, { expires: 30 });
                    setCookieForAllDomains('last_account_key', get().lastAccountKey ?? '');
                    setCookieForAllDomains('current_account_key', get().currentAccountKey ?? '');
                },
                switchAccount: (key: string) => {
                    const account = get().accounts[key];
                    if (!account) return false;

                    const expires = account.expires;
                    const token = account.token;

                    set({ token });
                    setCookieForAllDomains('token', token, { expires: new Date(expires) });
                    const currentAccountKey = get().currentAccountKey;

                    // Check if the account we're switching FROM is a temporary admin-impersonation account
                    const currentAccountData = currentAccountKey ? get().accounts[currentAccountKey] : null;
                    if (currentAccountData?.tmpLoginAsAdmin && currentAccountKey) {
                        // Remove the temporary account (immutable update)
                        const { [currentAccountKey]: _, ...remainingAccounts } = get().accounts;
                        set({ accounts: remainingAccounts });
                        removeCookieFromAllDomains(currentAccountKey);
                    } else {
                        set({ lastAccountKey: currentAccountKey });
                    }
                    setCookieForAllDomains('last_account_key', get().lastAccountKey ?? '');
                    set({ currentAccountKey: key });
                    setCookieForAllDomains('current_account_key', key);

                    // Reset all Zustand stores when switching accounts (matching Vue behavior)
                    queryClient.clear();

                    // Reset store-related stores
                    useStoreStore.getState().clearStore();

                    // Reset global store (session timezone matching Vue)
                    useGlobalStore.getState().setSessionTimezone('');
                    useGlobalStore.getState().clearAllLoading();

                    // Reset navigation store
                    useNavigationStore.setState({
                        dirtyComponents: new Set(),
                        bypassNextCheck: false,
                        blocker: null,
                        showConfirmation: false,
                        isMobileMoreModalOpen: false,
                    });

                    // Reset Ask Stanley store to initial state
                    const askStanleyStore = useAskStanleyStore.getState();
                    askStanleyStore.setChats([
                        {
                            id: -1,
                            title: ASK_STANLEY_CONSTANTS.DEFAULT_CHAT_TITLE,
                            messages: [],
                            loading: false,
                            isStreaming: false,
                            abortController: null,
                            created_at: new Date(),
                            updated_at: new Date(),
                        },
                    ]);
                    askStanleyStore.setCurrentChatId(-1);
                    askStanleyStore.setStatus('Thinking...');
                    askStanleyStore.setError(null);
                    askStanleyStore.setGeneratedPageData(undefined);
                    askStanleyStore.closeChat();

                    return true;
                },

                prepareToAddAccount: () => {
                    const oldCurrentAccountKey = get().currentAccountKey;
                    setCookieForAllDomains('last_account_key', oldCurrentAccountKey ?? '');

                    set({ lastAccountKey: oldCurrentAccountKey, currentAccountKey: null });
                    removeCookieFromAllDomains('current_account_key');
                },

                updateCurrentAccountWithUserData: (user: User) => {
                    const currentAccountKey = get().currentAccountKey;
                    if (!currentAccountKey) return;

                    const accounts = get().accounts;
                    const currentAccount = accounts[currentAccountKey];
                    if (!currentAccount) return;

                    // Calculate hasActiveSubscription using the same logic as Vue
                    let hasActiveSubscription = false;
                    if (user?.subscriptions?.platform) {
                        const platformSubscription = user.subscriptions.platform;
                        const currentTimestamp = Math.floor(new Date().getTime() / 1000);
                        const isStillValid = platformSubscription.valid_until
                            ? platformSubscription.valid_until > currentTimestamp
                            : false;
                        const activeStatuses = [
                            SUBSCRIPTION_STATUS.ACTIVE,
                            SUBSCRIPTION_STATUS.PENDING_ASSISTANCE,
                            SUBSCRIPTION_STATUS.DOWNGRADED,
                        ];

                        hasActiveSubscription =
                            activeStatuses.includes(platformSubscription.status as (typeof activeStatuses)[number]) ||
                            (platformSubscription.status === SUBSCRIPTION_STATUS.CANCELLED && isStillValid);
                    }

                    // Check if current user is an admin, 1 = admin, 4 = limited admin
                    const isAdmin = user?.list_of_roles?.includes(1) || user?.list_of_roles?.includes(4) || false;

                    // Update the current account with user metadata
                    const updatedAccount: Account = {
                        ...currentAccount,
                        email: user.email,
                        fullName: user.full_name,
                        user_id: user.user_id,
                        username: user.username,
                        image: user?.data?.profile_image || user?.profile_image,
                        admin: isAdmin,
                    };

                    // Update the accounts state
                    const updatedAccounts = { ...accounts, [currentAccountKey]: updatedAccount };
                    set({ accounts: updatedAccounts, hasActiveSubscription });

                    // Update cookies across all domains
                    const expires = new Date(updatedAccount.expires);
                    setCookieForAllDomains(currentAccountKey, JSON.stringify(updatedAccount), { expires });

                    Sentry.setUser({
                        user_id: user.user_id,
                        username: user.username,
                    });
                },

                // getters
                accountScopesInclude: (scopes, accountKey?: string): boolean => {
                    const scopesUse = scopes ? (typeof scopes === 'string' ? [scopes] : scopes) : [];

                    if (scopesUse.length === 0) return true;

                    const accountCheck = accountKey ? get().accounts[accountKey] : get().currentAccount();
                    if (!accountCheck) return false;

                    const hasScope = scopesUse.some(scope =>
                        getScopesFromToken(accountCheck?.token ?? '').includes(scope)
                    );

                    return hasScope;
                },
                isAdminLoggedInAsUser: () => {
                    // Matches Vue: current account must be a tmp account AND
                    // there must be another account that is an admin
                    const currentAccountKey = get().currentAccountKey;
                    const accounts = get().accounts;
                    if (!currentAccountKey || !accounts[currentAccountKey]?.tmpLoginAsAdmin) return false;

                    return Object.keys(accounts).some(key => key !== currentAccountKey && accounts[key]?.admin);
                },
                currentAccount: () => {
                    const currentAccountKey = get().currentAccountKey;
                    if (!currentAccountKey) return null;
                    return get().accounts[currentAccountKey] ?? null;
                },
                resetMFAToken: () => {
                    set({
                        mfaLoginEndpoint: undefined,
                        mfaToken: undefined,
                        mfaConfigName: undefined,
                    });
                },
            }),

            {
                name: 'auth-storage',
                partialize: state => ({
                    token: state.token,
                    isAuthenticated: state.isAuthenticated,
                }),
            }
        ),
        { name: 'auth-store' }
    )
);
